import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";

import { useAuth } from "../context/AuthContext";

import StandardScreen, {
  ScreenContent,
} from "../components/layout/StandardScreen";
import Stack from "../components/layout/Stack";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import FormField from "../components/forms/FormField";
import SelectInput from "../components/forms/SelectInput";

import {
  getPreauthSession,
  clearPreauthSession,
} from "../components/helpers/authFlowStorage";

const containerStyle = {
  width: "100%",
  maxWidth: "480px",
  margin: "0 auto",
  paddingTop: "var(--space-6)",
};

const mutedTextStyle = {
  margin: 0,
  fontSize: "var(--font-size-md)",
  color: "var(--color-text-muted)",
  textAlign: "center",
};

const inlineLinkButtonStyle = {
  border: 0,
  padding: 0,
  background: "transparent",
  color: "var(--color-accent)",
  font: "inherit",
  fontWeight: "var(--font-weight-semibold)",
  cursor: "pointer",
};

const backButtonStyle = {
  border: 0,
  padding: 0,
  background: "transparent",
  color: "var(--color-accent)",
  font: "inherit",
  fontWeight: "var(--font-weight-semibold)",
  cursor: "pointer",
};

const ExcursionGoto = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { applySession } = useAuth();

  const { phoneNumber, preauthToken } = getPreauthSession();

  const [role, setRole] = useState("member");
  const [excursions, setExcursions] = useState([]);
  const [selectedExcursionId, setSelectedExcursionId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingExcursions, setIsFetchingExcursions] = useState(false);
  const [loginCompleted, setLoginCompleted] = useState(false);

  useEffect(() => {
    if (loginCompleted) return;

    if (!phoneNumber || !preauthToken) {
      navigate("/login", { replace: true });
    }
  }, [phoneNumber, preauthToken, navigate, loginCompleted]);

  useEffect(() => {
    if (!phoneNumber || loginCompleted) return;

    const loadExcursions = async () => {
      try {
        setIsFetchingExcursions(true);

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/auth/matching-excursions`,
          {
            params: {
              phone_number: phoneNumber,
              role,
            },
          }
        );

        setExcursions(Array.isArray(res.data) ? res.data : []);
        setSelectedExcursionId("");
      } catch (error) {
        const msg =
          error.response?.data?.detail ||
          error.response?.data?.message ||
          t("excursion_goto.errors.load_failed");

        alert(msg);
        setExcursions([]);
        setSelectedExcursionId("");
      } finally {
        setIsFetchingExcursions(false);
      }
    };

    loadExcursions();
  }, [phoneNumber, role, loginCompleted, t]);

  const handleContinue = async () => {
    if (isLoading) return;

    if (!selectedExcursionId) {
      alert(t("excursion_goto.errors.select_excursion"));
      return;
    }

    const appId = `${phoneNumber}_${selectedExcursionId}_${role}`;

    try {
      setIsLoading(true);

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/login-final`,
        {
          app_id: appId,
          preauth_token: preauthToken,
        }
      );

      const accessToken = res.data?.access_token;
      const subscriptionStatus = String(
        res.data?.subscription_status || ""
      ).toLowerCase();

      if (!accessToken) {
        throw new Error("No access token returned");
      }

      await applySession({
        token: accessToken,
        role,
        activeAppId: appId,
        subscriptionStatus,
      });

      setLoginCompleted(true);
      clearPreauthSession();

      if (subscriptionStatus === "pending") {
        navigate("/pending", { replace: true });
        return;
      }

      navigate("/my-summary", { replace: true });
    } catch (error) {
      const msg =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        t("excursion_goto.errors.login_failed");

      if (
        msg === "Invalid or expired pre-auth token" ||
        msg === "Invalid pre-auth token type" ||
        msg === "Invalid pre-auth token subject" ||
        msg === "Phone mismatch"
      ) {
        clearPreauthSession();
        alert(t("excursion_goto.errors.session_expired"));
        navigate("/login", { replace: true });
        return;
      }

      alert(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const goRequestJoin = () => {
    navigate("/excursion-join", { replace: true });
  };

  const goBack = () => {
    navigate("/excursion-gate", { replace: true });
  };

  if (!phoneNumber || !preauthToken) return null;

  return (
    <StandardScreen background="default">
      <ScreenContent>
        <div style={containerStyle}>
          <Card
            title={t("excursion_goto.title")}
            subtitle={t("excursion_goto.subtitle")}
          >
            <Stack gap="4">
              <FormField label={t("excursion_goto.role_label")}>
                <SelectInput
                  value={role}
                  onChange={(e) => setRole(String(e.target.value))}
                  disabled={isLoading || isFetchingExcursions}
                >
                  <option value="member">
                    {t("excursion_goto.roles.member")}
                  </option>
                  <option value="admin">
                    {t("excursion_goto.roles.admin")}
                  </option>
                </SelectInput>
              </FormField>

              <FormField label={t("excursion_goto.select_label")}>
                <SelectInput
                  value={selectedExcursionId}
                  onChange={(e) =>
                    setSelectedExcursionId(String(e.target.value))
                  }
                  disabled={
                    isLoading || isFetchingExcursions || excursions.length === 0
                  }
                >
                  <option value="">
                    {isFetchingExcursions
                      ? t("excursion_goto.loading_excursions")
                      : t("excursion_goto.select_placeholder")}
                  </option>

                  {excursions.map((ex) => (
                    <option
                      key={ex.excursion_id}
                      value={String(ex.excursion_id)}
                    >
                      {`${ex.excursion_id} - ${ex.short_name}${
                        ex.start_date && ex.end_date
                          ? ` (${ex.start_date} → ${ex.end_date})`
                          : ""
                      }`}
                    </option>
                  ))}
                </SelectInput>
              </FormField>

              {!isFetchingExcursions && excursions.length === 0 ? (
                <p style={mutedTextStyle}>
                  {t("excursion_goto.no_excursions")}{" "}
                  <button
                    type="button"
                    onClick={goRequestJoin}
                    style={inlineLinkButtonStyle}
                  >
                    {t("excursion_goto.join_link")}
                  </button>
                  .
                </p>
              ) : null}

              <Button
                onClick={handleContinue}
                disabled={
                  isLoading || isFetchingExcursions || !selectedExcursionId
                }
                fullWidth
                size="lg"
              >
                {isLoading
                  ? t("excursion_goto.logging_in")
                  : t("excursion_goto.go_button")}
              </Button>

              <div style={{ textAlign: "center" }}>
                <button
                  type="button"
                  onClick={goBack}
                  style={backButtonStyle}
                  disabled={isLoading}
                >
                  {t("excursion_goto.back")}
                </button>
              </div>
            </Stack>
          </Card>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
};

export default ExcursionGoto;